import type { GameState, Theme, TileAnimation } from './types';
import { GRID } from './game';
import { t } from './i18n';

// ── Animation timing (exported so main.ts can clear state at the right time) ──

export const ANIM_SLIDE_MS = 100;
export const ANIM_POP_MS   = 80;
export const ANIM_TOTAL_MS = ANIM_SLIDE_MS + ANIM_POP_MS;

// ── Layout ────────────────────────────────────────────────────────────────────

export interface Layout {
  boardX:    number;
  boardY:    number;
  boardSize: number;
  cellSize:  number;
  gap:       number;
  scoreH:    number;
}

export function computeLayout(viewW: number, viewH: number): Layout {
  const scoreH    = Math.round(viewH * 0.13);
  const padding   = Math.round(Math.min(viewW, viewH) * 0.04);
  const available = Math.min(viewW, viewH - scoreH) - padding * 2;
  const boardSize = available;

  // (GRID+1) gaps (borders + between cells) + GRID cells = boardSize
  const GAP_RATIO = 0.030;
  const gap       = Math.round(boardSize * GAP_RATIO);
  const cellSize  = Math.round((boardSize - gap * (GRID + 1)) / GRID);

  const boardX = Math.round((viewW - boardSize) / 2);
  const boardY = scoreH + padding;

  return { boardX, boardY, boardSize, cellSize, gap, scoreH };
}

// ── Main render entry point ────────────────────────────────────────────────────

export function renderFrame(
  ctx:     CanvasRenderingContext2D,
  viewW:   number,
  viewH:   number,
  layout:  Layout,
  state:   GameState,
  theme:   Theme,
  now:     number,
  shakeX:  number,
): void {
  ctx.clearRect(0, 0, viewW, viewH);

  // Page background
  ctx.fillStyle = theme.pageBg;
  ctx.fillRect(0, 0, viewW, viewH);

  drawScore(ctx, viewW, layout.scoreH, state.score, state.bestScore, theme);

  // Apply horizontal shake offset to board + tiles only (score stays put)
  if (shakeX !== 0) {
    ctx.save();
    ctx.translate(shakeX, 0);
  }
  drawBoard(ctx, layout, theme);
  drawTiles(ctx, layout, state, theme, now);
  if (shakeX !== 0) {
    ctx.restore();
  }

  if (state.status !== 'playing') {
    drawOverlay(ctx, viewW, viewH, layout, state.status, theme);
  }
}

// ── Score strip ───────────────────────────────────────────────────────────────

function drawScore(
  ctx:       CanvasRenderingContext2D,
  viewW:     number,
  scoreH:    number,
  score:     number,
  bestScore: number,
  theme:     Theme,
): void {
  const BOX_W  = Math.min(Math.round(viewW * 0.34), 140);
  const BOX_H  = Math.round(scoreH * 0.60);
  const GAP    = 8;
  const BOX_R  = 6;
  const totalW = BOX_W * 2 + GAP;
  const startX = Math.round((viewW - totalW) / 2);
  const boxY   = Math.round((scoreH - BOX_H) / 2);

  function drawBox(x: number, label: string, value: number) {
    roundRect(ctx, x, boxY, BOX_W, BOX_H, BOX_R);
    ctx.fillStyle = theme.scoreBg;
    ctx.fill();

    ctx.textAlign    = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle    = theme.scoreLabel;
    ctx.font         = `${Math.round(BOX_H * 0.28)}px system-ui, sans-serif`;
    ctx.fillText(label, x + BOX_W / 2, boxY + Math.round(BOX_H * 0.10));

    ctx.fillStyle    = theme.scoreText;
    ctx.textBaseline = 'middle';
    ctx.font         = `bold ${Math.round(BOX_H * 0.42)}px system-ui, sans-serif`;
    const displayVal = value > 999999 ? '999999+' : String(value);
    ctx.fillText(displayVal, x + BOX_W / 2, boxY + Math.round(BOX_H * 0.68));
  }

  drawBox(startX,               'SCORE', score);
  drawBox(startX + BOX_W + GAP, 'BEST',  bestScore);
}

// ── Board background & empty cells ────────────────────────────────────────────

function drawBoard(
  ctx:    CanvasRenderingContext2D,
  layout: Layout,
  theme:  Theme,
): void {
  const { boardX, boardY, boardSize, cellSize, gap } = layout;
  const BOARD_R = Math.round(boardSize * 0.03);

  roundRect(ctx, boardX, boardY, boardSize, boardSize, BOARD_R);
  ctx.fillStyle = theme.boardBg;
  ctx.fill();

  const CELL_R = Math.round(cellSize * 0.08);
  ctx.fillStyle = theme.cellEmpty;

  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      const x = boardX + gap + c * (cellSize + gap);
      const y = boardY + gap + r * (cellSize + gap);
      roundRect(ctx, x, y, cellSize, cellSize, CELL_R);
      ctx.fill();
    }
  }
}

// ── Tile drawing with animation ───────────────────────────────────────────────

function drawTiles(
  ctx:    CanvasRenderingContext2D,
  layout: Layout,
  state:  GameState,
  theme:  Theme,
  now:    number,
): void {
  const { boardX, boardY, cellSize, gap } = layout;
  const anims   = state.animations;
  const elapsed = anims !== null ? now - state.animStartMs : Infinity;

  // Pixel position of cell top-left corner
  const cellPx = (col: number) => boardX + gap + col * (cellSize + gap);
  const cellPy = (row: number) => boardY + gap + row * (cellSize + gap);

  // rawSlideT: linear [0,1] over slide phase
  // slideT: eased with gentle back-overshoot → gives bounce at destination
  const rawSlideT = anims !== null ? Math.min(elapsed / ANIM_SLIDE_MS, 1) : 1;
  const slideT    = easeInOutCubic(rawSlideT);

  const animMap = new Map<number, TileAnimation>();
  if (anims) for (const a of anims) animMap.set(a.tileId, a);

  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';

  // ── Pass 1: Ghost tiles (merge-consumed) — drawn behind survivors ─────────
  // Only during slide phase; they fade out as they reach the destination.
  if (anims && elapsed < ANIM_SLIDE_MS) {
    for (const anim of anims) {
      if (anim.type !== 'merge-ghost') continue;
      const ghostVal = anim.ghostValue ?? 2;
      const x = cellPx(anim.fromCol) + (cellPx(anim.toCol) - cellPx(anim.fromCol)) * slideT;
      const y = cellPy(anim.fromRow) + (cellPy(anim.toRow) - cellPy(anim.fromRow)) * slideT;
      // Fade out the ghost: reaches 0 at rawSlideT ≈ 0.72
      const alpha = Math.max(0, 1 - rawSlideT * 1.4);
      drawTile(ctx, x, y, cellSize, ghostVal, alpha, 1, theme);
    }
  }

  // ── Pass 2: Regular tiles ─────────────────────────────────────────────────
  for (const tile of state.tiles) {
    const anim = animMap.get(tile.id);

    if (anim?.type === 'slide') {
      // Sliding tile — interpolated position throughout both phases
      const x = cellPx(anim.fromCol) + (cellPx(tile.col) - cellPx(anim.fromCol)) * slideT;
      const y = cellPy(anim.fromRow) + (cellPy(tile.row) - cellPy(anim.fromRow)) * slideT;
      drawTile(ctx, x, y, cellSize, tile.value, 1, 1, theme);

    } else if (anim?.type === 'merge-pop' && elapsed < ANIM_SLIDE_MS) {
      // Merge survivor: slide to destination during slide phase
      const x = cellPx(anim.fromCol) + (cellPx(tile.col) - cellPx(anim.fromCol)) * slideT;
      const y = cellPy(anim.fromRow) + (cellPy(tile.row) - cellPy(anim.fromRow)) * slideT;
      drawTile(ctx, x, y, cellSize, tile.value, 1, 1, theme);

    } else if (!anim) {
      // Stationary tile — draw at final grid position
      drawTile(ctx, cellPx(tile.col), cellPy(tile.row), cellSize, tile.value, 1, 1, theme);
    }
    // 'spawn' and 'merge-pop' in pop phase are handled in pass 3
  }

  // ── Pass 3: Pop-phase animations ──────────────────────────────────────────
  if (anims && elapsed >= ANIM_SLIDE_MS) {
    const rawT = Math.min((elapsed - ANIM_SLIDE_MS) / ANIM_POP_MS, 1);

    for (const tile of state.tiles) {
      const anim = animMap.get(tile.id);
      if (!anim) continue;

      const x = cellPx(tile.col);
      const y = cellPy(tile.row);

      if (anim.type === 'merge-pop') {
        // Bulge: 1.0 → 1.20 (peak at midpoint) → 1.0
        // sin-based: continuous from 1.0 at both ends, no discontinuity
        const scale = 1.0 + 0.22 * Math.sin(Math.PI * rawT);
        drawTile(ctx, x, y, cellSize, tile.value, 1, scale, theme);

      } else if (anim.type === 'spawn') {
        // Fade-in with scale-up
        const alpha = easeOutQuart(rawT);
        const scale = 0.5 + 0.5 * easeOutQuart(rawT);
        drawTile(ctx, x, y, cellSize, tile.value, alpha, scale, theme);
      }
    }
  }
}

// Draw a single tile. x,y = top-left corner of the cell slot at scale=1.
// scale > 1 expands from the center (for pop animations).
function drawTile(
  ctx:   CanvasRenderingContext2D,
  x:     number,
  y:     number,
  size:  number,
  value: number,
  alpha: number,
  scale: number,
  theme: Theme,
): void {
  const CELL_R = Math.round(size * 0.08);
  const bg     = theme.tileBg[value] ?? theme.tileBg[0];

  const needsRestore = scale !== 1 || alpha !== 1;
  if (needsRestore) {
    ctx.save();
    if (alpha !== 1) ctx.globalAlpha = alpha;
    if (scale !== 1) {
      const cx = x + size / 2;
      const cy = y + size / 2;
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      ctx.translate(-cx, -cy);
    }
  }

  roundRect(ctx, x, y, size, size, CELL_R);
  ctx.fillStyle = bg;
  ctx.fill();

  // Subtle 1 px border
  ctx.strokeStyle = theme.tileBorder;
  ctx.lineWidth   = 1;
  ctx.stroke();

  const textColor = theme.tileText[value] ?? theme.tileText[0] ?? '#000000';
  const fontSize  = value < 100   ? Math.round(size * 0.44)
                  : value < 1000  ? Math.round(size * 0.37)
                  : value < 10000 ? Math.round(size * 0.30)
                                  : Math.round(size * 0.25);

  ctx.fillStyle    = textColor;
  ctx.font         = `bold ${fontSize}px system-ui, sans-serif`;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(value), x + size / 2, y + size / 2);

  if (needsRestore) ctx.restore();
}

// ── Overlays ──────────────────────────────────────────────────────────────────

function drawOverlay(
  ctx:    CanvasRenderingContext2D,
  viewW:  number,
  viewH:  number,
  layout: Layout,
  status: 'won' | 'lost',
  theme:  Theme,
): void {
  const { boardX, boardY, boardSize } = layout;
  const BOARD_R = Math.round(boardSize * 0.03);

  roundRect(ctx, boardX, boardY, boardSize, boardSize, BOARD_R);
  ctx.fillStyle = status === 'won' ? theme.overlayWin : theme.overlayLose;
  ctx.fill();

  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle    = theme.overlayText;

  const centerX  = boardX + boardSize / 2;
  const centerY  = boardY + boardSize / 2;
  const headSize = Math.round(boardSize * 0.11);
  const subSize  = Math.round(boardSize * 0.055);

  ctx.font = `bold ${headSize}px system-ui, sans-serif`;
  ctx.fillText(status === 'won' ? t('win') : t('lose'),
    centerX, centerY - Math.round(headSize * 0.7));

  ctx.font = `${subSize}px system-ui, sans-serif`;
  ctx.fillText(t('tap_to_play'),
    centerX, centerY + Math.round(headSize * 0.7));

  void viewW; void viewH;
}

// ── Utilities ─────────────────────────────────────────────────────────────────

// Rounded rectangle path — uses native ctx.roundRect if available,
// falls back to arcTo for old Android WebViews.
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  w: number, h: number,
  r: number,
): void {
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y,     x + w, y + r,     r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x,     y + h, x,     y + h - r, r);
  ctx.lineTo(x,     y + r);
  ctx.arcTo(x,     y,     x + r, y,         r);
  ctx.closePath();
}

// Easing functions

function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

// Smooth symmetric S-curve — matches CSS ease-in-out feel.
// Tiles accelerate from rest, decelerate into the destination. No overshoot.
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
